import {
  FormControl,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { CalendarPickerView, DatePicker } from "@mui/x-date-pickers";

const valueTypeOptions = [
  "day, month and year",
  "month and year",
  "year only",
] as const;

export type DateValueType = (typeof valueTypeOptions)[number];

type DateOptionsProps = {
  date: Date | null;
  disabled?: boolean;
  groupLabel?: string;
  groupLabelSize?: number;
  onDateChange: (date: Date | null) => void;
  onValueTypeChange: (value: string) => void;
  valueType: DateValueType;
  maxDate?: Date;
  required?: boolean;
};

const defaultValueType = "day, month and year" as const;

export default function DateOptions({
  date,
  disabled,
  groupLabel,
  groupLabelSize = 2,
  onDateChange,
  onValueTypeChange,
  valueType,
  maxDate,
  required = false,
}: DateOptionsProps) {
  const getFormatAndViews = (): [string, CalendarPickerView[], string] => {
    // Default to year, month and day if no value type is provided
    if (!valueType || valueType === "day, month and year") {
      return ["dd/MM/yyyy", ["year", "month", "day"], "__/__/____"];
    }

    if (valueType === "month and year") {
      return ["MM/yyyy", ["year", "month"], "__/____"];
    }
    return ["yyyy", ["year"], "____"];
  };

  const [format, views, mask] = getFormatAndViews();

  return (
    <Grid container>
      {groupLabel && (
        <Grid item xs={groupLabelSize} alignContent="center">
          <FormLabel required={required}>{groupLabel}:</FormLabel>
        </Grid>
      )}
      <Grid item xs={12 - groupLabelSize}>
        <Stack direction="row" spacing={0.5}>
          <FormControl fullWidth>
            <InputLabel id="date-format">Date Format</InputLabel>
            <Select
              labelId="date-format"
              id="date-format-select"
              value={valueType || defaultValueType}
              onChange={(e) => {
                onValueTypeChange(e.target.value as DateValueType);
              }}
              disabled={disabled}
              variant="outlined"
              size="small"
              label="Date format"
            >
              {valueTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            disabled={disabled}
            renderInput={(props) => (
              <TextField
                fullWidth
                {...props}
                InputLabelProps={{ shrink: true }}
                helperText={null}
              />
            )}
            label="Date"
            value={date || null}
            onChange={onDateChange}
            maxDate={maxDate}
            inputFormat={format}
            views={views}
            mask={mask}
            disableMaskedInput={false}
          />
        </Stack>
      </Grid>
    </Grid>
  );
}
